import type {
    AnalyticsCfg,
    AnalyticsCfgFilter,
    AnalyticsCfgFilterComparition,
    AnalyticsCfgFilterMarketsField,
    FilterRelatedPeriodComparition,
    AnalyticsCfgFldType,
    RelationType,
} from './types-analytics';
import {
    AnalyticsCfgFilterMarketsFieldSchema,
    AnalyticsCfgFldTypeSchema,
    AnalyticsCfgSchema,
} from './types-analytics';
import type { QueriesType } from './types';
import type { API_DataType, API_ObjectAttributesType } from './types-telematics';

export const getFieldsForCluster = (cluster: string, cfg: AnalyticsCfg) => {
    return Object.entries(cfg.fld).filter(([key, values]) => values.cluster === cluster);
};

export const getFieldsNamesDBForCluster = (cluster: string, cfg: AnalyticsCfg) => {
    return getFieldsForCluster(cluster, cfg).map(([key, values]) => values.name_db);
};

export const getFilterObjFromAnalyticsFld = (
    fld: { [key: string]: AnalyticsCfgFldType },
    can_be_related: boolean
): API_ObjectAttributesType => {
    let key = Object.keys(fld)[0];
    let values = Object.values(fld)[0];

    try {
        values = AnalyticsCfgFldTypeSchema.parse(values);
        // console.log('validatedData', validatedData);
    } catch (error) {
        console.error(`ZodError, Error in field ${key}: ${error.message}`);
        // throw new Error(`Error in field ${key}: ${error.message}`);
    }

    const getReference = (fk: number, type_gm: string): string | null => {
        // When type_gm start with 'ext_' then it is a reference
        if (fk === 1) {
            return values.name_db;
        }
        if (type_gm && type_gm.startsWith('ext_')) {
            return values.name_db;
        }
        return null;
    };

    const getFilterable = (filter: string | number): boolean => {
        if (filter === '1' || filter === 1) {
            return true;
        }
        return false;
    };

    const getColorable = (color: string | number): boolean => {
        // Colorable will be true by default
        if (color === '0' || color === 0) {
            return false;
        }
        return true;
    };

    const getDefaultUnit = (name_orig: string, type_gm: string): string | null => {
        if (name_orig.startsWith('dist_') || name_orig.startsWith('c_dist_')) {
            return 'm';
        }
        if (type_gm === 'money') {
            return 'PLN';
        }
        if (type_gm === 'geometry') {
            return 'm2';
        }
        if (name_orig.match(/_percapita_/)) {
            return 'PLN';
        }
        if (name_orig.match(/_percent_/)) {
            return '%';
        }
        return '';
    };

    const getDataType = (type_gm: string): API_DataType => {
        if (type_gm === 'text') {
            return 'string';
        }
        if (type_gm === 'int') {
            return 'int';
        }
        if (type_gm === 'numeric') {
            return 'double';
        }
        if (type_gm === 'geo_area') {
            return 'geometry';
        }
        if (type_gm === 'geom') {
            return 'geometry';
        }
        if (type_gm === 'money') {
            return 'money';
        }
        if (type_gm === 'date') {
            return 'date';
        }
        if (type_gm && type_gm.startsWith('ext_id')) {
            return 'int';
        }
        throw new Error(`Unknown type_gm: ${type_gm}`);
        // return null;
    };

    const getNumberOrTimestamp = (value: string | number | null): number | null => {
        if (typeof value === 'undefined') {
            return null;
        }
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return new Date(value as string).getTime();
        }
        if (typeof value === 'string') {
            return Number(value);
        }
        throw new Error(`Unknown type of value: ${typeof value}`);
    };

    let apiObjectAttributes: API_ObjectAttributesType = {
        name: values.name_db,
        data_type: getDataType(values.type_gm),
        alarm: false,
        editable: false,
        colorable: getColorable(values.colorable ?? 1),
        filterable: getFilterable(values.filter),
        min: getNumberOrTimestamp(values?.min),
        max: getNumberOrTimestamp(values?.max),
        max_length: null,
        reference: getReference(values.fk, values.type_gm),
        unit:
            values?.unit !== ''
                ? values.unit
                : getDefaultUnit(values.name_orig ?? '', values.type_gm),
        filter_str: values.filter_str ?? '',
        subtype: values.subtype ?? null,
        desc: values?.desc || values?.label || values.name_db || '',
        info: values?.info || '',
        period_range: (values?.period_range as { min: string; max: string }) || null,
        collection: values?.collection || null,
        fld_period: values?.fld_period || null,
        can_be_related,
    };
    return apiObjectAttributes;
};

export const getClusters = (cfg: AnalyticsCfg): string[] => {
    let clusters: string[] = [];
    clusters = Object.keys(cfg.fld)
        .filter(key => typeof cfg.fld[key].cluster === 'string' && cfg.fld[key].cluster !== '')
        .map(key => {
            return String(cfg.fld[key].cluster);
        });
    // return unique clusters (dont use new Set)
    return clusters.filter((value, index, self) => self.indexOf(value) === index);
};

export const getClusterFields = (
    cluster: string,
    cfg: AnalyticsCfg
): { [key: string]: AnalyticsCfgFldType } => {
    try {
        cfg = AnalyticsCfgSchema.parse(cfg);
    } catch (error) {
        console.error(`ZodError, (getClusterFields) Error in cluster ${cluster}: ${error.message}`);
    }
    return Object.entries(cfg.fld)
        .filter(
            ([key, values]) =>
                values.cluster === cluster && (values.filter === '1' || values.filter === 1)
        )
        .reduce((obj, [key, value]) => {
            obj[key] = value;
            return obj;
        }, {});
};

export const getItemsForCluster = (
    clusterName: string,
    cfg: AnalyticsCfg
): { id: string; name: string; params?: Object }[] => {
    try {
        cfg = AnalyticsCfgSchema.parse(cfg);
    } catch (error) {
        console.error(
            `ZodError, (getItemsForCluster) Error in clusterName ${clusterName}: ${error.message}`
        );
    }
    return Object.keys(cfg.fld)
        .filter(key => cfg.fld[key].cluster === clusterName)
        .map(key => {
            return {
                id: cfg.fld[key].name_db,
                name: cfg.fld[key].desc,
                info: cfg.fld[key].info,
                params: cfg.fld[key].cluster_cfg,
            };
        });
};

export const getFilterFromAnalyticsFilter = (dbname: string, cfgFilter: AnalyticsCfgFilter) => {
    const filter = {};

    // try {
    //     cfgFilter = AnalyticsCfgFilterSchema.parse(cfgFilter);
    // } catch (error) {
    //     console.error(`ZodError, Error in $_CFG_FILTER for dbname: ${dbname}: ${error.message}`);
    // }

    Object.keys(cfgFilter[dbname][0]).forEach(key => {
        if (key == 'geom') {
            const v = cfgFilter[dbname][0][key];
            let geojson;
            if ('area' in v) {
                geojson = { type: 'MultiPolygon', coordinates: [], properties: { filter: 'area' } };
                for (let i = 0; i < v['area']['v'].length; i++) {
                    let poly = v['area']['v'][i]['data']
                        .split(' ')
                        .map(v => v.split(',').map(parseFloat));
                    poly.push(poly[0]);
                    geojson.coordinates.push([poly]);
                }
            } else if ('radius' in v) {
                geojson = {
                    type: 'MultiPolygon',
                    coordinates: [v['radius']['v'][0]['data']['poly']],
                    properties: {
                        filter: 'radius',
                        coords: v['radius']['v'][0]['data']['c'],
                        radius: v['radius']['v'][0]['data']['r'],
                        label: v['radius']['v'][0]['data']['lbl'],
                    },
                };
            }
            filter[key] = {
                ...geojson,
            };
            console.log('!!! geom', filter[key]);
        } else {
            let fld = cfgFilter[dbname][0][key];
            const { t, c, v }: AnalyticsCfgFilterMarketsField =
                AnalyticsCfgFilterMarketsFieldSchema.parse(fld);
            const negation = fld?.negation === true;

            if (typeof v === 'string') {
                if (negation) {
                    filter[key] = { $neq: v };
                } else {
                    filter[key] = { $eq: v };
                }
            } else if (['><'].includes(c) && v.length >= 2) {
                const [min, max] = v;
                switch (c) {
                    case '><':
                        switch (t) {
                            case 'date':
                                filter[key] = { $between: [String(min), String(max)] };
                                break;
                            default:
                                filter[key] = {
                                    $between: [parseFloat(<string>min), parseFloat(<string>max)],
                                };
                        }
                        break;
                    default:
                        break;
                }
            } else if (['>=', '<='].includes(c) && v.length >= 1) {
                let min = v[0];
                let max = v[v.length - 1];
                switch (c) {
                    case '<=': {
                        switch (t) {
                            case 'date':
                                filter[key] = { $lte: String(max) };
                                break;
                            default:
                                filter[key] = { $lte: parseFloat(<string>max) };
                        }
                        break;
                    }
                    case '>=': {
                        switch (t) {
                            case 'date':
                                filter[key] = { $gte: String(min) };
                                break;
                            default:
                                filter[key] = { $gte: parseFloat(<string>min) };
                        }
                        break;
                    }
                    default:
                        break;
                }
            } else {
                // convert all values in v to int
                v.forEach((val, i) => {
                    v[i] = parseInt(<string>val);
                });
                if (negation) {
                    filter[key] = { $nin: v };
                } else {
                    filter[key] = { $in: v };
                }
                // if (negation) {
                //     filter[key] = { $arr_nin: v };
                // } else {
                //     filter[key] = { $arr_in: v };
                // }
            }
        }
    });
    return { filter };
};

export const getAnalyticsFilterFromTelematicsFilter = (
    dbname: string,
    filter: { filter: QueriesType }
): AnalyticsCfgFilter => {
    const markets_dict: { [key: string]: AnalyticsCfgFilterMarketsField } = {};
    const join: [] = [];

    const isCorrectDateFormat = (str: string): boolean => {
        return /^\d{4}-\d{2}-\d{2}$/.test(str);
    };

    // markets_dict['empty'] = { t: "", v: "" }
    const getMarketsDict = (mongoCondition: any) => {
        let type = 'range';
        let market: AnalyticsCfgFilterMarketsField = { t: '', v: '' };
        if (typeof mongoCondition.$between !== 'undefined') {
            if (isCorrectDateFormat(String(mongoCondition.$between[0]))) {
                type = 'date';
            }
            market = {
                t: type,
                c: '><',
                v: [String(mongoCondition.$between[0]), String(mongoCondition.$between[1])],
            };
        } else if (typeof mongoCondition.$lte !== 'undefined') {
            if (isCorrectDateFormat(String(mongoCondition.$lte))) {
                type = 'date';
            }
            market = {
                t: type,
                c: '<=',
                v: [String(mongoCondition.$lte)],
            };
        } else if (typeof mongoCondition.$gte !== 'undefined') {
            if (isCorrectDateFormat(String(mongoCondition.$gte))) {
                type = 'date';
            }
            market = {
                t: type,
                c: '>=',
                v: [String(mongoCondition.$gte)],
            };
        } else if (typeof mongoCondition.$arr_in !== 'undefined') {
            market = {
                t: 'fk',
                negation: false,
                v: mongoCondition.$arr_in.map((item: any) => String(item)),
                l: mongoCondition.$arr_in.map((item: any) => String(item)),
            };
        } else if (typeof mongoCondition.$arr_nin !== 'undefined') {
            market = {
                t: 'fk',
                negation: true,
                v: mongoCondition.$arr_nin.map((item: any) => String(item)),
                l: mongoCondition.$arr_nin.map((item: any) => String(item)),
            };
        } else if (typeof mongoCondition.$in !== 'undefined') {
            market = {
                t: 'fk',
                negation: false,
                v: mongoCondition.$in.map((item: any) => String(item)),
                l: mongoCondition.$in.map((item: any) => String(item)),
            };
        } else if (typeof mongoCondition.$nin !== 'undefined') {
            market = {
                t: 'fk',
                negation: true,
                v: mongoCondition.$nin.map((item: any) => String(item)),
                l: mongoCondition.$nin.map((item: any) => String(item)),
            };
        } else if (typeof mongoCondition.$eq !== 'undefined') {
            market = {
                t: 'text',
                negation: false,
                v: String(mongoCondition.$eq),
            };
        } else if (typeof mongoCondition.$neq !== 'undefined') {
            market = {
                t: 'text',
                negation: true,
                v: String(mongoCondition.$neq),
            };
        }

        return market;
    };

    for (const key in filter.filter) {
        if (key == 'geom') {
            markets_dict[key] = {
                t: 'array',
            };
            let geomfiltertype = filter.filter[key]['properties']['filter'] ?? 'area'; // FIXME: default value but should be set in the schema
            let geomfiltervalue = [];
            switch (geomfiltertype) {
                case 'area':
                    for (let i = 0; i < filter.filter[key]['coordinates'].length; i++) {
                        geomfiltervalue.push({
                            type: 'poly',
                            data: filter.filter[key]['coordinates'][i][0]
                                .map(c => c.join(','))
                                .slice(0, -1)
                                .join(' '),
                        });
                    }
                    break;
                case 'radius':
                    console.log('geom2analytics', filter.filter[key]);
                    geomfiltervalue.push({
                        type: 'radius',
                        data: {
                            c: filter.filter[key]['properties'].coords,
                            l: '0',
                            r: filter.filter[key]['properties'].radius,
                            lbl: filter.filter[key]['properties'].label,
                            poly:
                                filter.filter[key]['type'] == 'MultiPolygon'
                                    ? filter.filter[key]['coordinates'][0]
                                    : filter.filter[key]['coordinates'],
                        },
                    });
                    break;
            }
            markets_dict[key][geomfiltertype] = { t: 'geometry', v: geomfiltervalue };
        } else {
            const mongoCondition = filter.filter[key];
            markets_dict[key] = getMarketsDict(mongoCondition);
        }
    }

    let cfgFilter: AnalyticsCfgFilter = {};
    cfgFilter[dbname] = [markets_dict];
    cfgFilter['join'] = join;
    return cfgFilter;
};

export const getAnalyticsJoinCompareFromFilterRelations = (
    relations: RelationType[]
): {
    join: any;
    compare: any;
} => {
    let join = [];
    let compare = [];

    const getRelatedJoinPeriodSimple = (collection: string, period: string) => {
        let ret = {};
        ret[collection] = [
            {
                c_period: {
                    t: 'date',
                    c: '==',
                    v: [period],
                },
            },
        ];
        return ret;
    };

    const getRelatedJoinValue = (
        period: string,
        fld_key: string,
        collection: string,
        c: AnalyticsCfgFilterComparition,
        v: (string | number)[]
    ) => {
        let ret = getRelatedJoinPeriodSimple(collection, period);
        ret[collection][0][fld_key] = {
            t: 'range',
            c: c,
            v: v,
        };
        return ret;
    };

    const getCompareRecordPeriod = (
        collection: string,
        base_period: string,
        second_period: string,
        fld_key: string,
        c: FilterRelatedPeriodComparition,
        v: (string | number)[],
        unit: string
    ) => {
        let relationFieldIndex1 = join.length;
        join.push(getRelatedJoinPeriodSimple(collection, second_period));

        let relationFieldIndex2 = join.length;
        join.push(getRelatedJoinPeriodSimple(collection, base_period));

        let fv = [];
        fv.push(unit);
        for (let i = 0; i < v.length; i++) {
            fv.push(v[i]);
        }

        let f = {};
        f[fld_key] = {
            t: 'range',
            c: c,
            v: fv,
            a: 'max',
        };

        let compare = {
            t: [relationFieldIndex1, relationFieldIndex2],
            f,
        };
        return compare;
    };

    relations.forEach((relation: RelationType) => {
        // console.warn('relation', relation.type);
        switch (relation.type) {
            case 'period': {
                compare.push(
                    getCompareRecordPeriod(
                        relation.db_name,
                        relation.base_period,
                        relation.second_period,
                        relation.id,
                        relation.c as FilterRelatedPeriodComparition,
                        relation.v,
                        relation.unit
                    )
                );
                break;
            }
            case 'value': {
                join.push(
                    getRelatedJoinValue(
                        relation.base_period,
                        relation.id,
                        relation.db_name,
                        relation.c as AnalyticsCfgFilterComparition,
                        relation.v
                    )
                );
                break;
            }
            default:
                throw new Error(`Unknown relation type: ${relation.type}`);
        }
    });

    return {
        join,
        compare,
    };
};

export const getFilterRelationsFromAnalyticsJoinCompare = (props: {
    join: any;
    compare: any;
}): RelationType[] => {
    let relations: RelationType[] = [];
    let join = props.join ?? [];
    let compare = props.compare ?? [];

    // get all values from property t of compare
    let compareIndexes = compare
        .map((compareRecord: any) => compareRecord.t)
        .flat()
        .map((v: string | number) => parseInt(v as string));

    if (typeof compare !== 'undefined') {
        compare.forEach((compareRecord: any) => {
            let db_name = Object.keys(join[parseInt(compareRecord.t[0])])[0];
            let comparing_field = Object.keys(join[parseInt(compareRecord.t[0])][db_name][0])[0];
            let base_period =
                join[parseInt(compareRecord.t[1])][db_name][0][comparing_field]['v'][0];
            let second_period =
                join[parseInt(compareRecord.t[0])][db_name][0][comparing_field]['v'][0];
            let id = Object.keys(compareRecord.f)[0];
            let c = compareRecord.f[id]['c'];
            let v = compareRecord.f[id]['v'];
            let unit = compareRecord.f[id]['v'][0];

            relations.push({
                id,
                type: 'period',
                c,
                unit,
                v: v.slice(1),
                db_name,
                base_period,
                second_period,
                comparing_field,
                a: 'max',
            });
        });
    }

    if (typeof join !== 'undefined') {
        join.forEach((joinRecord: any, index: number) => {
            if (compareIndexes.includes(index)) {
                return;
            }
            let db_name = Object.keys(joinRecord)[0];
            let comparing_field = Object.keys(joinRecord[db_name][0])[0];
            let base_period = joinRecord[db_name][0][comparing_field]['v'][0];
            let id = Object.keys(joinRecord[db_name][0])[1];
            let c = joinRecord[db_name][0][id]['c'];
            let v = joinRecord[db_name][0][id]['v'];

            relations.push({
                id,
                type: 'value',
                c,
                unit: '', // empty, bcs we need to get this later using id and availableTypes
                v: v,
                db_name,
                base_period,
                second_period: '',
                comparing_field,
                a: 'max',
            });
        });
    }

    return relations;
};
